import { FormGroup, ValidationErrors } from '@angular/forms';
import { DynamicFormFields, FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormMediatorFormState } from '@core/data-access/models/form/form-mediator.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { Person } from '@core/data-access/models/person.model';
import { BuyerBillingAddressFormState } from '@core/features/buyer-information/models/buyer-billing-address-form.model';
import { BuyerInformationFormState } from '@core/features/buyer-information/models/buyer-information-form.model';
import { BuyerPrivacyPolicyFormState } from '@core/features/buyer-information/models/buyer-privacy-policy-form.model';
import { BuyerQuestionnaireFormState } from '@core/features/buyer-questionnaire/models/buyer-questionnaire-fom.model';
import {
  TicketHolderFormState,
  TicketHolderFormStates
} from '@core/features/ticket-assignment/models/ticket-holder-form.model';
import {
  TicketHolderQuestionnaireFormState,
  TicketHolderQuestionnaireFormStates
} from '@core/features/ticket-assignment/models/ticket-holder-questionnaire-fom.model';
import { TicketHolder } from '@core/features/ticket-assignment/models/ticket-holder.model';
import { TariffUuid } from '@products/models/tariff.model';
import { StepName } from '@store/step-forms/step.interface';
import { Observable } from 'rxjs';

export const BuyerInformationStepFormValidityName = {
  [FormName.Registration]: [StepName.BuyerInformation, FormName.Registration],
  [FormName.BuyerInformation]: [StepName.BuyerInformation, FormName.BuyerInformation],
  [FormName.BuyerBillingAddress]: [StepName.BuyerInformation, FormName.BuyerBillingAddress],
  [FormName.BuyerPrivacyPolicy]: [StepName.BuyerInformation, FormName.BuyerPrivacyPolicy]
} as const;

export const BuyerQuestionnaireStepFormValidity = {
  [FormName.BuyerQuestionnaire]: [StepName.BuyerQuestionnaire, FormName.BuyerQuestionnaire]
} as const;

export const TicketAssignmentStepFormValidity = {
  [FormName.TicketHolder]: [StepName.TicketAssignment, FormName.TicketHolder],
  [FormName.TicketHolderQuestionnaire]: [StepName.TicketAssignment, FormName.TicketHolderQuestionnaire]
};

export const FormStatus = {
  VALID: 'VALID',
  INVALID: 'INVALID',
  PENDING: 'PENDING',
  DISABLED: 'DISABLED'
} as const;

export enum FormError {
  TicketLimitPerEmailAddress = 'ticketLimitPerEmailAddress',
  DuplicateName = 'duplicateName',
  DuplicateEmailAddress = 'duplicateEmailAddress'
}

export type CheckBuyerInformationValidityForms =
  | BuyerInformationFormState
  | BuyerBillingAddressFormState
  | BuyerPrivacyPolicyFormState;

export type CheckBuyerInformationFormsValidity = Record<FormName, CheckBuyerInformationValidityForms>;

export type CheckBuyerQuestionnaireValidityForms = BuyerQuestionnaireFormState;

export type CheckBuyerQuestionnaireFormsValidity = Record<FormName, CheckBuyerQuestionnaireValidityForms>;

export type CheckTicketAssignmentValidityForms = TicketHolderFormStates | TicketHolderQuestionnaireFormStates;

export type CheckTicketAssignmentsFormsValidity = Record<FormName, CheckTicketAssignmentValidityForms>;

export type CheckMergedTicketAssignmentFormsValidity = CheckTicketAssignmentsFormsValidity;

export type CheckedTariffNameValidity = Record<TariffUuid, InvalidFormField>;

export type CheckedTariffEmailAddressValidity = Record<TariffUuid, InvalidFormField>;

export interface CheckedSelectedTicketAssignmentFormsAndFormFieldsValidity {
  areAllTicketAssignmentStepFormsValidAndValidationIsNotProcessing: boolean;
  nextInvalidTicketAssignmentFormTariffUuid: TariffUuid;
  checkedFormsValidity: CheckedFormsValidity;
}

export interface CheckedOrderOverviewFormAndFormFieldsValidity {
  ticketHolder: TicketHolder;
  formValidity: FormValidity;
}

export type SetCreateFormGroupsFormMediatorForms = Record<
  FormName | TariffUuid,
  FormMediatorFormState | TicketHolderFormState | TicketHolderQuestionnaireFormState
>;

export type CreatedFormGroups = Record<
  FormName | TariffUuid,
  Observable<{ dynamicFormFields: DynamicFormFields; formGroup: FormGroup; person: Person }>
>;

export type CheckedFormsValidity = Partial<Record<FormName, FormValidity>>;

export interface FormValidity {
  invalidFormFields: InvalidFormFields;
  formErrors: Partial<ValidationErrors>;
  isValid: boolean;
}

export type InvalidFormFields = Partial<Record<FieldKey, InvalidFormField>>;

export type InvalidFormField = {
  label: string;
  value: string | boolean;
  errors: ValidationErrors;
};

export type GroupedNameEmailAddressAndPersonsByTariffUuids = Record<
  TariffUuid,
  {
    person: Person;
    name: string;
    email: string;
  }
>;
